import {
  PublicListVendorResponse,
  CustomerProductServiceListCustomerProductQueryParams,
  VoucherServiceListVoucherkuQueryParams,
  PublicListVoucherkuResponse,
  SharedProductTypeEnum,
  PublicBulkValidateVoucherResponse,
  PublicListCustomerProductResponse,
  PublicListCustomerPackageCollectionResponse,
  CustomerPackageCollectionServiceListCustomerPackageCollectionQueryParams,
} from '@bukalapak/openapi-schema/vptrader/product/v1/public/product-domain';
import {
  PublicCreateCustomerEWalletTransactionResponse,
  PublicCreateCustomerEWalletTransactionRequest,
  PublicCreateCustomerEWalletInquiryRequest,
  PublicCreateCustomerEWalletInquiryResponse,
  PublicCreateCustomerPrepaidPhoneTransactionResponse,
  PublicCreateCustomerPrepaidPhoneTransactionRequest,
  CustomerSubscriptionServiceGetCustomerSubscriptionInstantRefundEligibilityQueryParams,
  PublicCreateCustomerSubscriptionInstantRefundRequest,
  PublicCreateCustomerSubscriptionInstantRefundResponse,
  PublicGetCustomerSubscriptionInstantRefundEligibilityResponse,
  CustomerTransactionServiceListCustomerTransactionsQueryParams,
  PublicListCustomerTransactionsResponse,
} from '@bukalapak/openapi-schema/vptrader/transaction/v1/public/transaction-domain';

import {
  PublicCreateCustomerTransactionInquiryRequest,
  PublicCreateCustomerTransactionInquiryResponse,
  PublicCreateCustomerTransactionRequest,
  PublicCreateCustomerTransactionResponse,
  PublicGetCustomerTransactionResponse,
} from '@bukalapak/openapi-schema/vptrader/transaction/v2/public/transaction-domain';

import { PublicGetUserLoanDetailsResponse, PublicGetDisbursementResponse, PublicListRepaymentsResponse } from '@bukalapak/openapi-schema/vptrader/loan/v1/public/loan-domain';

import accessTokenManagerInstance from '~/api/v4/access-token-manager-instance';
import wrapSecureClientInterface from './v4';

import VPNonTraderCustomerTransactionAPI from './v4/modules/vpnontrader/customer-transaction-api';
import VPNonTraderCustomerTransactionV2API from './v4/modules/vpnontrader/customer-transaction-v2-api';
import VPNonTraderVendorAPI from './v4/modules/vpnontrader/vendors-api';
import VPNonTraderCustomerProductAPI from './v4/modules/vpnontrader/customer-products-api';
import VPNonTraderVoucherAPI from './v4/modules/vpnontrader/voucher-api';
import VPNonTraderCustomerPackageCollectionsAPI from './v4/modules/vpnontrader/customer-package-collections-api';
import VPNonTraderLoanAPI from './v4/modules/vpnontrader/loan-api';

const VPNonTraderAPIClient = {
  v4: {
    accessTokenManager: accessTokenManagerInstance,
  } as any,

  init() {
    this.v4.vpnontraderVendor = wrapSecureClientInterface(
      accessTokenManagerInstance,
      VPNonTraderVendorAPI,
      'vpnontrader_vendor'
    );
    this.v4.vpnontraderCustomerTransaction = wrapSecureClientInterface(
      accessTokenManagerInstance,
      VPNonTraderCustomerTransactionAPI,
      'vpnontrader_customer_transaction'
    );
    this.v4.vpnontraderCustomerTransactionV2 = wrapSecureClientInterface(
      accessTokenManagerInstance,
      VPNonTraderCustomerTransactionV2API,
      'vpnontrader_customer_transaction_v2'
    );
    this.v4.vpnontraderCustomerProduct = wrapSecureClientInterface(
      accessTokenManagerInstance,
      VPNonTraderCustomerProductAPI,
      'vpnontrader_customer_product'
    );
    this.v4.vpnontraderVoucher = wrapSecureClientInterface(
      accessTokenManagerInstance,
      VPNonTraderVoucherAPI,
      'vpnontrader_voucher'
    );
    this.v4.vpnontraderCustomerPackageCollection = wrapSecureClientInterface(
      accessTokenManagerInstance,
      VPNonTraderCustomerPackageCollectionsAPI,
      'vpnontrader_customer_package_collections'
    );
    this.v4.vpnontraderLoan = wrapSecureClientInterface(
      accessTokenManagerInstance,
      VPNonTraderLoanAPI,
      'vpnontrader_loan'
    );
  },

  getPrepaidPhoneTopupTransaction(id: number): PublicGetCustomerTransactionResponse {
    return this.v4.vpnontraderCustomerTransaction.getPrepaidPhoneTopupTransaction(id);
  },

  getEWalletTransaction(id: number): PublicGetCustomerTransactionResponse {
    return this.v4.vpnontraderCustomerTransaction.getEWalletTransaction(id);
  },

  createPrepaidPhoneTransaction(payload): PublicCreateCustomerPrepaidPhoneTransactionResponse {
    const requestPayload: PublicCreateCustomerPrepaidPhoneTransactionRequest = {
      order_id: payload.orderId,
      product_code: payload.productCode,
      phone_number: payload.phoneNumber,
      payment_method_type: payload.paymentMethodType, // deprecated
      balance_payment_method_type: payload.balancePaymentMethodType,
      voucher_code: payload.voucherCode,
      price_after_promotion: payload.priceAfterPromotion,
    };
    return this.v4.vpnontraderCustomerTransaction.createPrepaidPhoneTransaction(requestPayload);
  },

  createEWalletTransaction(payload): PublicCreateCustomerEWalletTransactionResponse {
    const requestPayload: PublicCreateCustomerEWalletTransactionRequest = {
      order_id: payload.orderId,
      product_code: payload.productCode,
      phone_number: payload.phoneNumber,
      payment_method_type: payload.paymentMethodType, // deprecated
      balance_payment_method_type: payload.balancePaymentMethodType,
      voucher_code: payload.voucherCode,
      amount: payload.amount,
    };
    return this.v4.vpnontraderCustomerTransaction.createEWalletTransaction(requestPayload);
  },

  createEWalletInquiry(payload): PublicCreateCustomerEWalletInquiryResponse {
    const requestPayload: PublicCreateCustomerEWalletInquiryRequest = {
      product_code: payload.productCode,
      phone_number: payload.phoneNumber,
      amount: payload.amount,
    };
    return this.v4.vpnontraderCustomerTransaction.createEWalletInquiry(requestPayload);
  },

  createCustomerTransactionInquiry(payload): PublicCreateCustomerTransactionInquiryResponse {
    const requestPayload: PublicCreateCustomerTransactionInquiryRequest = {
      customer_number: payload.customer_number,
      product_code: payload.product_code,
      transaction_type: payload.transaction_type,
      options: payload.options,
    };

    return this.v4.vpnontraderCustomerTransactionV2.createCustomerTransactionInquiry(requestPayload);
  },

  createCustomerTransaction(payload): PublicCreateCustomerTransactionResponse {
    const requestPayload: PublicCreateCustomerTransactionRequest = {
      customer_number: payload.customer_number,
      product_code: payload.product_code,
      order_id: payload.order_id,
      transaction_type: payload.transaction_type,
      balance_payment_method_type: payload.balance_payment_method_type,
      voucher_code: payload.voucher_code,
      options: payload.options,
    };

    return this.v4.vpnontraderCustomerTransactionV2.createCustomerTransaction(requestPayload);
  },

  getVendors(productTypes: String, phoneNumber: String): PublicListVendorResponse {
    const requestPayload = {
      product_type: productTypes,
      phone_number: phoneNumber,
      limit: 100,
    };

    return this.v4.vpnontraderVendor.getVendors(requestPayload);
  },

  getCustomerProducts(
    payload: CustomerProductServiceListCustomerProductQueryParams
  ): PublicListCustomerProductResponse {
    return this.v4.vpnontraderCustomerProduct.getCustomerProducts(payload);
  },

  getVoucherku(productTypes: SharedProductTypeEnum, limit: number, offset: number): PublicListVoucherkuResponse {
    const requestPayload: VoucherServiceListVoucherkuQueryParams = {
      limit,
      offset,
      product_type: productTypes,
    };

    return this.v4.vpnontraderVoucher.getVoucherku(requestPayload);
  },

  bulkValidateVoucher(
    voucherCodes: string[],
    productCode: string,
    paymentMethod: string,
    requestAmount: string
  ): PublicBulkValidateVoucherResponse {
    const requestPayload = {
      voucher_codes: voucherCodes,
      product_code: productCode,
      payment_method: paymentMethod,
      ...{ request_amount: requestAmount || undefined },
    };

    return this.v4.vpnontraderVoucher.bulkValidateVoucher(requestPayload);
  },

  getCustomerPackageCollections(
    payload: CustomerPackageCollectionServiceListCustomerPackageCollectionQueryParams
  ): PublicListCustomerPackageCollectionResponse {
    return this.v4.vpnontraderCustomerPackageCollection.getCustomerPackageCollections(payload);
  },

  createCustomerSubscriptionInstantRefund(payload): PublicCreateCustomerSubscriptionInstantRefundResponse {
    const requestPayload: PublicCreateCustomerSubscriptionInstantRefundRequest = {
      transaction_id: payload.transaction_id,
    };
    return this.v4.vpnontraderCustomerTransaction.createCustomerSubscriptionInstantRefund(requestPayload);
  },

  getCustomerSubscriptionInstantRefundEligibility(
    payload: CustomerSubscriptionServiceGetCustomerSubscriptionInstantRefundEligibilityQueryParams
  ): PublicGetCustomerSubscriptionInstantRefundEligibilityResponse {
    return this.v4.vpnontraderCustomerTransaction.getCustomerSubscriptionInstantRefundEligibility(payload);
  },

  listTransactions(payload: CustomerTransactionServiceListCustomerTransactionsQueryParams): PublicListCustomerTransactionsResponse {
    return this.v4.vpnontraderCustomerTransaction.listTransactions(payload);
  },

  getUserLoanDetails(): PublicGetUserLoanDetailsResponse {
    return this.v4.vpnontraderLoan.getUserLoanDetails();
  },

  getDisbursement(id: string): PublicGetDisbursementResponse {
    return this.v4.vpnontraderLoan.getDisbursement(id);
  },

  listRepayments(payload: { disbursementId: string; limit: number; offset: number }): PublicListRepaymentsResponse {
    return this.v4.vpnontraderLoan.listRepayments(payload.disbursementId, {
      limit: payload.limit,
      offset: payload.offset,
    });
  },
};

VPNonTraderAPIClient.init();

export default VPNonTraderAPIClient;
